import { ActiveNavLink } from "components/common/ActiveNavLink";
import { CommentPanel } from "components/common/comments/CommentPanel";
import { HierarchyContext } from "contexts/HierarchyContext";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Breadcrumb } from "react-bootstrap";
import { ArrowUpSquare } from "react-bootstrap-icons";
import { getHierarchyBaseUrl } from "../../contexts/hierarchy/getHierarchyBaseUrl";
import { getThisAndParentHierarchies } from "../../functions/getParentHierarchies";

/**
 * Breadcrumb navigation that displays the current hierarchy path.
 *
 * The component measures available width and collapses into a compact
 * form when there is insufficient space.
 *
 * @returns Breadcrumb JSX or null when no hierarchy is available.
 */
export const HierarchyCrumbs = (): React.ReactElement | null => {
  const hierarchyContext = useContext(HierarchyContext);
  const breadcrumbRef = useRef<HTMLDivElement>(null);
  const [fullWidth, setFullWidth] = useState<number>(0);
  const [measuredNodes, setMeasuredNodes] = useState<number>(0);
  const [hideTree, setHideTree] = useState(false);

  const hierarchies: { name: string; id: string; url: string }[] = useMemo(
    () =>
      hierarchyContext && hierarchyContext.hierarchy
        ? [
            hierarchyContext.hierarchy,
            ...getThisAndParentHierarchies(
              hierarchyContext.hierarchy!.parentId,
              hierarchyContext.hierarchyNodes,
            ),
          ]
            .map((h) => ({ name: h.name, id: h.id, url: getHierarchyBaseUrl(h) }))
            .reverse()
        : [],
    [hierarchyContext],
  );

  // Get expanded size
  useEffect(() => {
    const breadcrumb = breadcrumbRef.current;
    const container = breadcrumb?.parentElement;
    if (
      breadcrumb &&
      container &&
      !hideTree &&
      (measuredNodes !== hierarchies.length || fullWidth === 0)
    ) {
      // Get max width
      setFullWidth(breadcrumb.offsetWidth);
      setMeasuredNodes(hierarchies.length);
    }
  }, [fullWidth, hideTree, hierarchies.length, measuredNodes]);

  // Add resize observer
  useEffect(() => {
    // Get parent element (which changes size with document)
    const container = breadcrumbRef.current?.parentElement;
    if (container && fullWidth) {
      // Set resize observer to check for overflow
      /**
       * Check container width and toggle compact breadcrumb mode when necessary.
       */
      const checkContainerSize = () => {
        setHideTree(container.offsetWidth < fullWidth + 40);
      };
      const resizeObserver = new ResizeObserver(checkContainerSize);
      resizeObserver.observe(container);
      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [fullWidth]);

  return !hierarchyContext?.hierarchy ? null : (
    <Breadcrumb
      ref={breadcrumbRef}
      className="text-nowrap d-block"
    >
      {hideTree && hierarchies.length >= 2 && (
        <Breadcrumb.Item
          id={`breadcrumbs-up`}
          key={`breadcrumbs-up`}
          linkAs={ActiveNavLink}
          linkProps={{
            to: `${hierarchies[hierarchies.length - 2].url}`,
            end: true,
          }}
        >
          <ArrowUpSquare />
        </Breadcrumb.Item>
      )}
      {hierarchies.slice(hideTree ? hierarchies.length - 1 : 0).map((hierarchy) => (
        <Breadcrumb.Item
          id={`breadcrumbs-${hierarchy.name}`}
          key={hierarchy.id}
          linkAs={ActiveNavLink}
          linkProps={{
            to: `${hierarchy.url}`,
            end: true,
          }}
        >
          {hierarchy.name}
        </Breadcrumb.Item>
      ))}
      <CommentPanel activeContext="Hierarchy" />
    </Breadcrumb>
  );
};

HierarchyCrumbs.displayName = "HierarchyCrumbs";
